/**
 * TRAC-550 B8: Auto-retry scheduler with exponential backoff + jitter.
 *
 * Replaces the TRAC-81 fixed 15s countdown timer with 4 retries spaced at
 * 5s/8s/13s/21s ± 20% jitter (≈50s total budget). Auto-retry is the model:
 * NO Try-Again button is shown during the retry window.
 *
 * Decision rationale (per pre-mortem):
 *   200 prod concurrent users × 10 retries × 5s = 400 RPS into the survivor
 *   region — would thunder-herd. Exponential backoff with jitter spreads
 *   the load and reduces total retry count to 4 while preserving the same
 *   ~50s recovery budget.
 */

const BASE_DELAYS_MS = [5_000, 8_000, 13_000, 21_000] as const;
const JITTER_PCT = 0.2;
export const MAX_RETRIES = BASE_DELAYS_MS.length;

/**
 * Returns the delay (ms) before the Nth retry attempt (0-indexed). Pure;
 * jitter source is injected so tests can pin it.
 */
export function computeRetryDelayMs(
  attempt: number,
  random: () => number = Math.random,
): number {
  if (attempt < 0 || attempt >= BASE_DELAYS_MS.length) {
    throw new Error(`Retry attempt out of range: ${attempt}`);
  }
  const base = BASE_DELAYS_MS[attempt];
  // jitter ∈ [-JITTER_PCT, +JITTER_PCT]
  const jitter = (random() * 2 - 1) * JITTER_PCT;
  return Math.round(base * (1 + jitter));
}

export interface RetrySchedulerCallbacks {
  /** Invoked when it's time to make the actual retry attempt. */
  doRetry: () => void;
  /** Invoked when all attempts have been exhausted (no more retries scheduled). */
  onExhausted: () => void;
  /** Optional progress hook; useful for surfacing the attempt count to UI. */
  onAttempt?: (nextAttempt: number, delayMs: number) => void;
}

export interface RetryScheduler {
  /** Schedule the next attempt. attemptIndex is 0-based (0 = first retry). */
  schedule(attemptIndex: number): void;
  /** Hard-abandon the retry loop. Idempotent — safe to call repeatedly. */
  abandon(): void;
  /** Test-only inspection. */
  pendingAttempt(): number | null;
}

/**
 * Creates a stateful scheduler. Holds at most one in-flight setTimeout at
 * a time. abandon() clears it; schedule() replaces it.
 */
export function createRetryScheduler(
  callbacks: RetrySchedulerCallbacks,
  random: () => number = Math.random,
): RetryScheduler {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let pending: number | null = null;

  const clear = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    pending = null;
  };

  return {
    schedule(attemptIndex: number) {
      clear();
      if (attemptIndex >= MAX_RETRIES) {
        callbacks.onExhausted();
        return;
      }
      const delayMs = computeRetryDelayMs(attemptIndex, random);
      pending = attemptIndex;
      callbacks.onAttempt?.(attemptIndex, delayMs);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        pending = null;
        callbacks.doRetry();
      }, delayMs);
    },
    abandon() {
      clear();
    },
    pendingAttempt() {
      return pending;
    },
  };
}
